<template>
    <div class="colors" ref="colorsContainer">
        <span v-for="c in colorElements" :class="c" :key="c"></span>
    </div>

    <component :is="chartComponent"
               :data="chartData"
               :options="chartOptions" />
</template>


<script setup>
    import { ref, computed, onMounted } from 'vue'
    import {
        Bar, Doughnut, Line, Pie, Radar
    } from 'vue-chartjs'
    import {
        Chart as ChartJS,
        Title,
        Tooltip,
        Legend,
        BarElement,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        RadialLinearScale,
        ArcElement
    } from 'chart.js'

    ChartJS.register(
        Title, Tooltip, Legend,
        BarElement, CategoryScale, LinearScale,
        PointElement, LineElement, RadialLinearScale, ArcElement
    )
    // Default tooltip styles
    Object.assign(ChartJS.defaults.plugins.tooltip, {
        backgroundColor: 'rgba(255,255,255,1)',
        bodyColor: '#000',
        titleColor: '#000',
        borderWidth: 1,
        borderColor: 'rgba(0,0,0,1)',
        cornerRadius: 0,
        bodySpacing: 6,
        bodyFont: { family: "'GT Eesti', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif" },
        padding: 10
    })
    ChartJS.defaults.color = "#000"
    
    // --- Props
    const props = defineProps({
        chartType: { type: String, default: 'bar-chart' },
        chartItems: { type: Array, default: () => [] },
        showLegend: { type: Boolean, default: true },
        legendPosition: { type: String, default: 'top' },
        useLegendFiltering: { type: Boolean, default: true },
        descriptionId: { type: String, default: '' },
        chartTitle: { type: String, default: '' }
    })

    // --- Local state
    const colorsContainer = ref(null)
    const defaultColors = [
        'outotec-black', 'outotec-grey', 'outotec-orange',
        'outotec-salmon', 'outotec-purple', 'outotec-lavender', 'outotec-pink'
    ]

    // --- Computed & methods (converted)
    const chartRawData = computed(() =>
        Array.isArray(props.chartItems) ? props.chartItems : []
    )

    const colorElements = computed(() => {
        const colors = []
        chartRawData.value.forEach((f, i) => {
            if (!f.color) f.color = defaultColors[i] || null
            if (!colors.includes(f.color)) colors.push(f.color)
        })
        return colors
    })

    const colors = computed(() => {
        const dict = {}
        if (!colorsContainer.value) return dict
        Array.from(colorsContainer.value.children).forEach(f => {
            const style = getComputedStyle(f)
            dict[f.classList[0]] = style.getPropertyValue('color')
        })
        return dict
    })

    const hasData = computed(() =>
        Array.isArray(chartRawData.value) && chartRawData.value.length > 0
    )

    // Chart type mapping
    const chartComponent = computed(() => {
        switch (props.chartType) {
            case 'bar-chart': return Bar
            case 'line-chart': return Line
            case 'pie-chart': return Pie
            case 'doughnut-chart': return Doughnut
            case 'radar-chart': return Radar
            default: return Bar
        }
    })
    function getLineChartData(fill = true) {
        const colorDic = colors.value
        const data = { labels: [], datasets: [] }

        chartRawData.value.forEach((f) => {
            if (!data.labels.includes(f.dataPointLabel)) data.labels.push(f.dataPointLabel)

            const existing = data.datasets.find(ds => ds.label === f.dataGroupName)
            if (existing) {
                existing.data.push(f.data)
                if (fill) existing.backgroundColor.push(colorDic[f.color])
            } else {
                data.datasets.push({
                    fill,
                    label: f.dataGroupName,
                    backgroundColor: fill ? [colorDic[f.color]] : colorDic[f.color],
                    borderColor: fill ? undefined : colorDic[f.color],
                    data: [f.data],
                    tension: 0,
                    borderWidth: 0
                })
            }
        })
        return data
    }

    function getPieChartData() {
        const colorDic = colors.value
        const data = { labels: [], datasets: [{ backgroundColor: [], borderWidth: 0, data: [] }] }

        chartRawData.value.forEach((f) => {
            if (!data.labels.includes(f.dataPointLabel)) data.labels.push(f.dataPointLabel)
            data.datasets[0].data.push(f.data)
            data.datasets[0].backgroundColor.push(colorDic[f.color])
        })
        return data
    }

    function getRadarChartData() {
        const d = getLineChartData(true)
        d.datasets.forEach((f) => {
            const color = Array.isArray(f.backgroundColor) ? f.backgroundColor[0] : f.backgroundColor
            f.pointHoverBackgroundColor = color
            f.borderColor = color
            f.pointBackgroundColor = color
            f.pointBorderColor = color
            f.backgroundColor = color?.replace(')', ', 0.2)').replace('rgb', 'rgba') ?? f.backgroundColor
        })
        return d
    }

    const chartData = computed(() => {
        switch (props.chartType) {
            case 'bar-chart':
                return getLineChartData(true)
            case 'line-chart':
                return getLineChartData(false)
            case 'pie-chart':
            case 'doughnut-chart':
                return getPieChartData()
            case 'radar-chart':
                return getRadarChartData()
            default:
                return getLineChartData(true)
        }
    })

    function isPrint() {
        return window.matchMedia && window.matchMedia('print').matches
    }

    const chartOptions = computed(() => {
        const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: props.showLegend,
                    position: props.legendPosition
                }
            }
        }

        if (!props.useLegendFiltering && options.plugins.legend) {
            options.plugins.legend.onClick = e => e.stopPropagation()
        }

        if (isPrint()) options.animation = false
        if (props.chartType === 'pie-chart') options.aspectRatio = 1

        // Scale setup for bar/line
        if (
            (props.chartType === 'bar-chart' || props.chartType === 'line-chart') &&
            chartData.value.datasets?.length > 0
        ) {
            options.aspectRatio = 1
            const limits = chartData.value.datasets.reduce((c, a) => {
                const arr = a.data || [0]
                const current = { min: Math.min(...arr), max: Math.max(...arr) }
                if (!c) return current
                return { min: Math.min(c.min, current.min), max: Math.max(c.max, current.max) }
            }, null)

            const minTick = limits && limits.min < 0 ? limits.min : 0
            options.scales = {
                y: {
                    min: minTick,
                    ticks: {
                        callback(value) {
                            return value + ' -'
                        }
                    }
                },
                x: {
                    grid: { display: false },
                }
            }
        }

        return options
    })
</script>